import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackWebsiteVisit } from '../../api/tracking/trackingAPI';
import { extractCampaignParams } from './trackingUtils';

/**
 * AdsWebsiteVisit Component
 * 
 * A component that automatically tracks website visits when campaign parameters are present in the URL.
 * This component should be placed in routes where you want to track ad-driven visits.
 * 
 * Usage:
 * <AdsWebsiteVisit />
 * 
 * The component will:
 * - Check for campaign parameters in the URL (utm_* or campaign_id)
 * - Automatically send a tracking request if parameters are found
 * - Handle errors gracefully without affecting the user experience
 */
const AdsWebsiteVisit = () => {
  const location = useLocation();

  useEffect(() => {
    const trackVisit = async () => {
      try {
        // Extract campaign parameters from current URL
        const campaignParams = extractCampaignParams(window.location.href);
        
        // Only track if we have campaign identifier
        if (campaignParams.cmpg_identifier) {
          // Prepare visit data - only cmpg_identifier and referrer domain
          const visitData = {
            cmpg_identifier: campaignParams.cmpg_identifier,
            referrer: document.referrer ? new URL(document.referrer).hostname : null,
          };

          // Track the visit (fire-and-forget)
          trackWebsiteVisit(visitData);
        }
      } catch (error) {
        // Silent error handling - tracking errors don't break the app
      }
    };

    // Track visit when component mounts or location changes
    trackVisit();
  }, [location]);

  // This component doesn't render anything
  return null;
};

export default AdsWebsiteVisit;
